import { useCallback, useEffect, useRef, useState } from "react";
import { ActionIcon, Badge, Container, Group, Loader, MultiSelect, TagsInput, TextInput, Button, Stack, Modal, Tooltip } from "@mantine/core";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";
import HttpClient from "../helpers/HttpClient";
import { reportExport } from "../helpers/auditExport";
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';
import { IconBrandWhatsapp, IconChevronUp, IconDownload, IconFileReport, IconPencil, IconPhone, IconPlus, IconSelector } from "@tabler/icons-react";
import dayjs from 'dayjs';
import { Person } from "../types/person.type";
import { useUserContext } from "../providers/UserContext";
import { useLanguage } from "../providers/LanguageContext";
import { Tag } from "../types/tag.type";

interface PersonFilters {
  page: number;
  pageSize: number;
  tagIds: number[];
  excludeTagIds: number[];
  sort: DataTableSortStatus<Person>;
}

function Mapping() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialTagNames] = useState<string[]>(() =>
    searchParams.get('tags')?.split(',').filter(Boolean) ?? []
  );
  // null = no `exclude` param in the URL -> use the default ("Inaktiv").
  // An empty param means the user deliberately removed all exclusions.
  const [initialExcludeNames] = useState<string[] | null>(() =>
    searchParams.has('exclude')
      ? searchParams.get('exclude')!.split(',').filter(Boolean)
      : null
  );

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<Person[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const { userRights } = useUserContext();

  const [nameInput, setNameInput] = useState<string>(searchParams.get('name') ?? '');
  const [debouncedName] = useDebouncedValue(nameInput, 300);
  const prevDebouncedNameRef = useRef(debouncedName);

  const [filters, setFilters] = useState<PersonFilters>({
    page: Number(searchParams.get('page') ?? '1'),
    pageSize: 20,
    tagIds: [],
    excludeTagIds: [],
    sort: {
      columnAccessor: searchParams.get('sort') ?? 'lastName',
      direction: (searchParams.get('dir') ?? 'asc') as 'asc' | 'desc',
    },
  });

  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [excludedTags, setExcludedTags] = useState<Tag[]>([]);
  // The first fetch waits for the tags, so the default exclusion is already
  // applied and inactive persons don't flash up before being filtered out.
  const [tagFiltersReady, setTagFiltersReady] = useState(false);

  const [selectedPersons, setSelectedPersons] = useState<Person[]>([]);
  const [bulkDialogOpened, setBulkDialogOpened] = useState(false);
  const [bulkTags, setBulkTags] = useState<string[]>([]);

  const { t } = useLanguage();
  const p = t.person;
  const navigate = useNavigate();

  const fetchData = useCallback(async (f: PersonFilters, name: string) => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        skip: String((f.page - 1) * f.pageSize),
        take: String(f.pageSize),
      };
      if (name.trim()) params.name = name.trim();
      if (f.tagIds.length) params.tagIds = f.tagIds.join(',');
      if (f.excludeTagIds.length) params.excludeTagIds = f.excludeTagIds.join(',');

      const result = await new HttpClient<Person>('/person').getAllPaged(params);
      let data = result.data ?? [];
      // Client-side sort within page when sorting by non-default column
      if (f.sort.columnAccessor !== 'lastName' || f.sort.direction !== 'asc') {
        data = [...data].sort((a: any, b: any) => {
          const av = a[f.sort.columnAccessor] ?? '';
          const bv = b[f.sort.columnAccessor] ?? '';
          const cmp = String(av).localeCompare(String(bv));
          return f.sort.direction === 'desc' ? -cmp : cmp;
        });
      }
      setRecords(data);
      setTotalRecords(result.total);
    } catch (error) {
      console.error("API call failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    new HttpClient<Tag>('/tag').getAll()
      .then(tags => {
        setAllTags([...tags].sort((a, b) => a.name.localeCompare(b.name)));
        if (tags.length === 0) setTagFiltersReady(true);
      })
      // Never block the list on the tags: without them, just load unfiltered.
      .catch(() => setTagFiltersReady(true));
  }, []);

  const defaultExcludedTags = allTags.filter(t => t.name.trim().toLowerCase() === 'inaktiv');

  // Restore tag filters from the URL snapshot (or apply the default exclusion)
  // once the tag list is available.
  useEffect(() => {
    if (allTags.length === 0) return;
    const restored = allTags.filter(t => initialTagNames.includes(t.name));
    const excluded = initialExcludeNames === null
      ? defaultExcludedTags
      : allTags.filter(t => initialExcludeNames.includes(t.name));
    setSelectedTags(restored);
    setExcludedTags(excluded);
    setFilters(prev => ({
      ...prev,
      tagIds: restored.map(t => t.id),
      excludeTagIds: excluded.map(t => t.id),
      page: restored.length || excluded.length ? 1 : prev.page,
    }));
    setTagFiltersReady(true);
  }, [allTags]);

  // Single combined fetch effect — handles both name-debounce and filter changes
  useEffect(() => {
    const nameChanged = prevDebouncedNameRef.current !== debouncedName;
    prevDebouncedNameRef.current = debouncedName;

    if (nameChanged && filters.page !== 1) {
      setFilters(prev => ({ ...prev, page: 1 }));
      return;
    }

    if (!tagFiltersReady) return;
    fetchData(filters, debouncedName);
  }, [debouncedName, filters, tagFiltersReady]);

  // Sync URL params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (nameInput) params.name = nameInput;
    if (selectedTags.length) params.tags = selectedTags.map(t => t.name).join(',');
    // Only written when it differs from the default, so plain links stay clean.
    const excludeNames = excludedTags.map(t => t.name).sort().join(',');
    const defaultNames = defaultExcludedTags.map(t => t.name).sort().join(',');
    if (tagFiltersReady && excludeNames !== defaultNames) params.exclude = excludeNames;
    if (filters.page !== 1) params.page = String(filters.page);
    if (filters.sort.columnAccessor !== 'lastName') params.sort = filters.sort.columnAccessor;
    if (filters.sort.direction !== 'asc') params.dir = filters.sort.direction;
    setSearchParams(params, { replace: true });
  }, [nameInput, selectedTags, excludedTags, tagFiltersReady, filters.page, filters.sort]);

  // Tag filters accept typed input: resolve each entry to an existing tag
  // (case-insensitive); text that matches no tag can't filter anything and is dropped.
  const resolveTags = (values: string[]): Tag[] => {
    const wanted = new Set(values.map(v => v.trim().toLowerCase()));
    return allTags.filter(tag => wanted.has(tag.name.toLowerCase()));
  };

  const handleTagChange = (values: string[]) => {
    const newTags = resolveTags(values);
    setSelectedTags(newTags);
    setFilters(prev => ({ ...prev, tagIds: newTags.map(t => t.id), page: 1 }));
  };

  const handleExcludeTagChange = (values: string[]) => {
    const newTags = resolveTags(values);
    setExcludedTags(newTags);
    setFilters(prev => ({ ...prev, excludeTagIds: newTags.map(t => t.id), page: 1 }));
  };

  const handleExportCsv = () => {
    const escape = (v: string | undefined | null) => {
      const s = v ?? '';
      return s.includes(';') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const escapePhone = (v: string | undefined | null) => v ? `="${v}"` : '';

    const headers = [
      'PersonId', 'Vorname', 'Nachname', 'Geburtsdatum', 'Geburtsort', 'Nationalität',
      'Straße', 'PLZ', 'Ort', 'E-Mail', 'Telefon 1', 'Telefon 2',
      'Notfallkontakt', 'Notfalltelefon', 'Tags',
    ];

    const rows = selectedPersons.map(p => [
      escape(String(p.id)),
      escape(p.firstName),
      escape(p.lastName),
      p.birthDate ? dayjs(p.birthDate).format('DD.MM.YYYY') : '',
      escape(p.birthPlace),
      escape(p.nationality),
      escape(p.street),
      escape(p.zip),
      escape(p.city),
      escape(p.email),
      escapePhone(p.phone1),
      escapePhone(p.phone2),
      escape(p.emergencyContact),
      escapePhone(p.emergencyPhone),
      escape(p.tags?.map(t => t.name).join(', ')),
    ]);

    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `personen-export-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    reportExport('person-list-csv', { personIds: selectedPersons.map(p => p.id!), fields: headers });
  };

  const handleBulkAssignTags = async () => {
    if (selectedPersons.length === 0 || bulkTags.length === 0) return;

    const selectedTagObjects = allTags.filter(tag => bulkTags.includes(tag.name));

    try {
      const httpClient = new HttpClient<Person>('/person');

      for (const person of selectedPersons) {
        const existingTags = person.tags || [];
        const existingTagIds = new Set(existingTags.map(t => t.id));
        const newTags = selectedTagObjects.filter(t => !existingTagIds.has(t.id));
        const mergedTags = [...existingTags, ...newTags];

        const { hasValidContract, balance, attendance, ...personData } = person;
        void attendance; // computed by the list endpoint, not writable
        await httpClient.post({
          ...personData,
          tagIds: mergedTags.map(t => t.id),
          tags: mergedTags,
        } as Person);
      }

      fetchData(filters, debouncedName);
      setBulkDialogOpened(false);
      setBulkTags([]);
      setSelectedPersons([]);
    } catch (error) {
      console.error("Failed to assign tags:", error);
    }
  };

  return (
    <>
        <Modal opened={bulkDialogOpened} onClose={() => setBulkDialogOpened(false)} size="sm" radius="md" withinPortal={false} // Ensure the modal is rendered within the correct DOM hierarchy
        centered>
      <Stack gap="md">
        <div>
          <h3>{p.bulkTitle}</h3>
          <span style={{ fontSize: '0.9rem', color: '#666' }}>
            {p.bulkDesc} {selectedPersons.length} {p.persons}
          </span>
        </div>
        <MultiSelect
          label={p.bulkTagsLabel}
          data={allTags.map(tag => tag.name)}
          value={bulkTags}
          placeholder={p.selectTags}
          onChange={setBulkTags}
          searchable
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={() => setBulkDialogOpened(false)}>
            {p.cancel}
          </Button>
          <Button onClick={handleBulkAssignTags} disabled={bulkTags.length === 0}>
            {p.assign}
          </Button>
        </Group>
      </Stack>
    </Modal>
    <Container fluid pb={10} mt={10}>
        <Group gap={25} visibleFrom="xs" >
          <h1>{p.title}</h1>
          <ActionIcon variant="filled" size="xl" radius="xl" aria-label="Settings" onClick={() => navigate('/person/create')}>
            <IconPlus style={{ width: '70%', height: '70%' }} stroke={1.5} />
          </ActionIcon>
        </Group>
        <TextInput
          placeholder={p.filterName}
          value={nameInput}
          onChange={(e) => setNameInput(e.currentTarget.value)}
          mb="md"
        />
        <TagsInput
          label={p.filterTags}
          data={allTags.map(tag => tag.name)}
          value={selectedTags.map(tag => tag.name)}
          placeholder={p.selectTags}
          onChange={handleTagChange}
          clearable
          acceptValueOnBlur
          mb="sm"
        />
        <TagsInput
          label={p.excludeTags}
          data={allTags.map(tag => tag.name)}
          value={excludedTags.map(tag => tag.name)}
          placeholder={p.selectTags}
          onChange={handleExcludeTagChange}
          clearable
          acceptValueOnBlur
          mb="md"
        />
        {selectedPersons.length > 0 && (
          <Group mb="md">
            <Button onClick={() => setBulkDialogOpened(true)} variant="light">
              {p.assignTagsBtn} {selectedPersons.length} {p.persons}
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              color="blue"
              onClick={handleExportCsv}
            >
              {p.csvExport} ({selectedPersons.length})
            </Button>
            {(userRights.includes('accounting') || userRights.includes('admin')) && (
              <Button
                leftSection={<IconFileReport size={16} />}
                variant="light"
                color="teal"
                onClick={() => {
                  const ids = selectedPersons.map(person => person.id).join(',');
                  window.open(`/booking/report?persons=${ids}&autoprint=1`, '_blank');
                }}
              >
                {p.accountReport} ({selectedPersons.length})
              </Button>
            )}
            {(userRights.includes('accounting') || userRights.includes('admin')) && (
              <Button
                leftSection={<IconFileReport size={16} />}
                variant="light"
                color="grape"
                onClick={() => {
                  const ids = selectedPersons.map(person => person.id).join(',');
                  window.open(`/booking/report/summary?persons=${ids}&autoprint=1`, '_blank');
                }}
              >
                {p.summary} ({selectedPersons.length})
              </Button>
            )}
          </Group>
        )}
    </Container>
    <Container fluid>
      {loading && records.length === 0 ? (
        <Loader size="md" />
      ) : (
        <DataTable
              withTableBorder
              borderRadius="sm"
              withColumnBorders
              striped
              highlightOnHover
              fetching={loading}
              selectedRecords={selectedPersons}
              onSelectedRecordsChange={setSelectedPersons}
              records={records}
              columns={[
                {
                  accessor: 'Contact',
                  title: '',
                  textAlign: 'left',
                  width: '5%',
                  render: ({ phone1 }) =>
                    phone1 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          onClick={() => window.open(`tel:${phone1}`, '_self')}
                          aria-label="Call"
                        >
                          <IconPhone size={18} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          onClick={() => window.open(`https://wa.me/${phone1}`, '_self')}
                          aria-label="Call"
                        >
                          <IconBrandWhatsapp size={18} />
                        </ActionIcon>
                      </span>
                    ),
                },
                {
                  accessor: 'firstName',
                  title: p.colFirstName,
                  textAlign: 'left',
                  width: '10%'
                },
                {
                  accessor: 'lastName',
                  title: p.colLastName,
                  textAlign: 'left',
                  width: '10%',
                  sortable: true,
                  render: ({ lastName, createdAt, updatedAt }) => {
                    const sevenDaysAgo = dayjs().subtract(7, 'day');
                    const isNew = createdAt && dayjs(createdAt).isAfter(sevenDaysAgo);
                    const isUpdated = !isNew && updatedAt && dayjs(updatedAt).isAfter(sevenDaysAgo);
                    return (
                      <Group gap={6} wrap="nowrap">
                        <span>{lastName}</span>
                        {isNew && <Badge size="xs" variant="light" color="green">Neu</Badge>}
                        {isUpdated && <Badge size="xs" variant="light" color="orange">Aktualisiert</Badge>}
                      </Group>
                    );
                  },
                },
                {
                  accessor: 'birthDate',
                  title: p.colBirthDate,
                  textAlign: 'center',
                  width: '5%',
                  sortable: true,
                  render: ({ birthDate }) => birthDate ? dayjs(birthDate).format('DD.MM.YYYY') : '',
                },
                {
                  accessor: 'tags',
                  title: p.colTags,
                  textAlign: 'left',
                  width: '50%',
                  render: ({ tags }) =>
                    Array.isArray(tags) && tags.length > 0
                      ? (
                          <Group gap={4} wrap="wrap">
                            {[...tags].sort((a, b) => a.name.localeCompare(b.name)).map(tag => (
                              <Badge key={tag.id} variant="filled" color={tag.color}>
                                {tag.name}
                              </Badge>
                            ))}
                          </Group>
                        )
                      : '',
                },
                {
                  accessor: 'hasValidContract',
                  title: p.colContract,
                  width: '10%',
                  render: ({ hasValidContract }) =>
                    hasValidContract ? p.contractYes : p.contractNo,
                },
                {
                  accessor: 'attendance',
                  title: p.colTraining,
                  textAlign: 'center',
                  width: '8%',
                  // Current month, non-cancelled trainings up to today; "–" if none yet.
                  render: ({ attendance }) =>
                    attendance ? (
                      <Tooltip
                        label={p.trainingTooltip
                          .replace('{present}', String(attendance.present))
                          .replace('{total}', String(attendance.total))}
                      >
                        <Badge
                          variant="light"
                          color={attendance.pct >= 75 ? 'green' : attendance.pct >= 50 ? 'yellow' : 'red'}
                        >
                          {attendance.pct} %
                        </Badge>
                      </Tooltip>
                    ) : (
                      '–'
                    ),
                },
                ...(userRights.includes("accounting")
                ? [{
                              accessor: 'balance',
                              title: p.colBalance,
                              textAlign: 'right' as const,
                              sortable: true,
                              width: '5%',
                              render: ({ balance }: Person) =>
                                balance?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }),
                  }]
                : []),
                {
                  accessor: 'Action',
                  title: '',
                  textAlign: 'center',
                  width: '2%',
                  render: ({ id }) => (
                    <ActionIcon
                      variant="light"
                      color="blue"
                      onClick={() => navigate(`/person/edit/${id}`)}
                      aria-label="Edit"
                    >
                      <IconPencil size={18} />
                    </ActionIcon>
                  ),
                },
              ]}
              totalRecords={totalRecords}
              recordsPerPage={filters.pageSize}
              recordsPerPageOptions={[10, 20, 50, 100]}
              recordsPerPageLabel={p.recordsPerPage}
              onRecordsPerPageChange={(pageSize) => setFilters(prev => ({ ...prev, pageSize, page: 1 }))}
              page={filters.page}
              onPageChange={(p) => setFilters(prev => ({ ...prev, page: p }))}
              sortStatus={filters.sort}
              onSortStatusChange={(s) => setFilters(prev => ({ ...prev, sort: s, page: 1 }))}
              sortIcons={{
                sorted: <IconChevronUp size={14} />,
                unsorted: <IconSelector size={14} />,
              }}
        />
      )}
    </Container>
  </>
  )
}

export default Mapping;
